import {
  Button,
  Divider,
  Grid,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import React from 'react';
import update from 'react-addons-update';
import { Link } from 'react-router-dom';

import {
  BrandSafetyRuleActiveActive,
  BrandSafetyRuleActiveInactive,
  BrandSafetyRuleTargetingTypeBidder,
  BrandSafetyRuleTargetingTypeBidderGroup,
  BrandSafetyRuleTargetingTypeChannel,
  BrandSafetyRuleTargetingTypeChannelGroup,
  BrandSafetyRuleTargetingTypeGlobal,
  IBrandSafetyRule,
  targetingTypeToString,
} from '~/src/dash/api/brand-safety-rules';
import {
  BrandSafetyTrackerActiveActive,
  BrandSafetyTrackerActiveInactive,
  getBrandSafetyTrackers,
  getTrackerEventTargeting,
  getTrackerThirdParty,
  IBrandSafetyTracker,
  updateBrandSafetyTracker,
} from '~/src/dash/api/brand-safety-tracker';
import { getSitesPromise } from '~/src/dash/api/channels';
import { IHeaderBidder, ISite, queryHeaderBidders } from '~/src/dash/api/console-api';
import {
  getCreativeValidationsV2,
  ICreativeValidation,
  REVIEW_STATUS_ALLOWED,
  REVIEW_STATUS_BLOCKED,
  REVIEW_STATUS_BLOCKED_BY_API,
  REVIEW_STATUS_REVIEWED,
  REVIEW_STATUS_UNREVIEWED,
  updateCreativeValidation,
} from '~/src/dash/api/creative-validation';
import {
  addDomainAndTokenToUrl,
  addQueryParams,
  createUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
} from '~/src/dash/api/rest';
import {
  ICreativeValidationRowData,
  IListTableColumn,
  ListTable,
} from '~/src/dash/components/list-table';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { SortByMode } from '~/src/dash/components/material-search-dropdown-multi-list';
import { MenuButton } from '~/src/dash/components/menu-button';
import EditCreativeReviewModal from '~/src/dash/components/modal-dialog-edit-creative-review';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelSecondaryButton } from '~/src/dash/components/model-secondary-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { ITab, SettingsTabs } from '~/src/dash/components/settings-tabs';
import { Panel, Panels, spacing } from '~/src/dash/publicaUI';
import { Col, Cols } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteBrandSafetyEdit,
  RouteBrandSafetyNew,
  RouteBrandSafetyTrackerEdit,
  RouteBrandSafetyTrackerNew,
  setStringFromParam,
} from '~/src/dash/router/routes';
import { FindSelectedIDsFromMenuList, IMenuList } from '~/src/dash/types/menuList';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { getRuleTypeChip } from '~/src/dash/utils/brand-safety-chip';
import { publicaColors } from '~/src/dash/utils/colors';
import { decorate } from '~/src/dash/utils/decorator';
import { IHeadCell } from '~/src/dash/utils/headCell';
import { genBiddersMenuList } from '~/src/dash/utils/header-bidder-adapters';
import { getAllIABCategories, IAB_CATEGORIES } from '~/src/dash/utils/iab-category';
import { IABCategoryCodesToLabels } from '~/src/dash/utils/iab-category';
import { deleteColor } from '~/src/dash/utils/material-dashboard-react';
import { filterSiteByPrebid } from '~/src/dash/utils/models';
import {
  IPermissionCheck,
  IS_AUTHORIZED,
  notAllowedEmptyIPermissionCheck,
} from '~/src/dash/utils/permission_service';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
import NotesDrawer from '~/src/dash/views/brand-safety-notes-drawer';
import { RowButton } from '~/src/dash/views/component-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState extends IBaseState {
  // rule list states
  loading: boolean;
  loadingTrackers: boolean;
  reviewOrderBy: number;
  updatingReviewOrderby: boolean;
  updatingReviewStatus: boolean;
  // queryParams state
  queryParams: URLSearchParams;
  // delete popup
  deletePopupActive: boolean;
  deletePopupItemID: string;
  deletePopupItemName: string;
  // rules list
  rules: IBrandSafetyRule[];
  // snackbar - used to display small notification
  snackbarMessage: string; // message → the snackbar will automatically be displayed if this message is not empty
  snackbarError: boolean; // error → will turn the snackbar red
  // list sorting
  orderDirection: 'asc' | 'desc';
  orderBy: string;
  // search
  searchFilter: string; // user search query
  // pagination
  page: number; // current page number
  pageTrackers: number; // current page number
  rowsPerPage: number; // number of entries per page
  rowsPerPageTrackers: number; // number of entries per page
  // permissions
  hasPublisherReadRight: IPermissionCheck;
  hasPublisherWriteRight: IPermissionCheck;
  hasChannelReadRight: IPermissionCheck;
  hasChannelWriteRight: IPermissionCheck;
  hasBidderReadRight: IPermissionCheck;
  hasBidderWriteRight: IPermissionCheck;
  // trackers list
  trackers: IBrandSafetyTracker[];
  // lists
  siteList: IMenuList;
  bidderList: IMenuList;
  bidderTierList: IMenuList;
  demandSourceList: IMenuList;
  // creative_validation list related fields
  adomainFilter: string;
  categoryFilter: string;
  creativeURLFilter: string;
  reviewStatusFilter: number;
  siteFilter: string;
  bidderFilter: string;
  bidderTierFilter: string;
  demandSourceFilter: string;
  openEditCreativeReviewModal: boolean;
  updatingCreativeReview: boolean;
  // IAB Category
  IABCategoryPrefix: string;
  IABCategorySuffix: string;
  IABSubCategoryList: IMenuList;
  // current tab
  currentTab: string;
  // Notes
  openNotesDrawer: boolean;
  creativeData: ICreativeValidationRowData;
  updatingNotes: boolean;
  // variables used by debounceGetCreativeValidations
  forceAPIRefresh: boolean;
}

// define the rules list table columns (like active, name, type ... etc.)
const rulesListColumns: IHeadCell[] = [
  { id: 'IsActive', numeric: false, label: 'Active' },
  { id: 'Name', numeric: false, label: 'Rule Name' },
  { id: 'Type', numeric: false, label: 'Type' },
  { id: 'Targeting', numeric: false, label: 'Targeting' },
  { id: 'NbrResponseBlocked', numeric: true, label: 'Responses Blocked' },
  // the delete button column is automatically added at the end of those
];

// define the tracker list table columns (like active, name, ... etc.)
const trackerListColumns: IHeadCell[] = [
  { id: 'Active', numeric: false, label: 'Active' },
  { id: 'Name', numeric: false, label: 'Name' },
  { id: 'ThirdParty', numeric: false, label: 'Third Party' },
  { id: 'Events', numeric: false, label: 'Events' },
  { id: 'MeasuredInventory', numeric: true, label: 'Scale' },
  // the delete button column is automatically added at the end of those
];

const ReviewOrderByNone = 0;
const ReviewOrderByBlockedResponses = 3;
const ReviewOrderByImpressions = 4;

// -------------------- HELPER FUNCTIONS --------------------
const transformCreativeValidationToRowData = (
  cv: ICreativeValidation,
): ICreativeValidationRowData => {
  return {
    PublisherID: cv.PublisherID,
    Adomain: cv.Adomain,
    IABCategory: IABCategoryCodesToLabels(cv.IABCategory, ';'),
    IABCategoryCodes: cv.IABCategory,
    OriginalCreativeURL: cv.OriginalCreativeURL,
    CreativeURL: cv.CreativeURL,
    Impressions: cv.Impressions,
    BidErrors: cv.BidErrors,
    BidErrorBlockedResponses: cv.BidErrorBlockedResponses,
    BidErrorsBrandSafety: cv.BidErrorsBrandSafety,
    BidderID: cv.BidderID,
    ReviewStatus: cv.ReviewStatus,
    Notes: cv.Notes,
    BrandSafetyRuleIDs: cv.BrandSafetyRuleIDs,
  } as ICreativeValidationRowData;
};

// -------------------- COMPONENT DECLARATION --------------------
export const BrandSafetyListView = decorate(
  {
    // ---- REDUX
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // ---- STYLE DECLARATION
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
        overflow: 'scroll',
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      tooltipSmall: {
        fontSize: 10,
      },
      tooltipBold: {
        fontSize: 12,
        fontWeight: 500,
      },
      textField: {
        margin: 0,
        minWidth: '400px',
        width: '30%',
      },
      mainTab: {
        '&:hover': {
          background: '#d7d8fc 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          color: '#4952FF',
          fontSize: '14px',
          fontWeight: '500',
        },
        color: '#707070',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
      },
      mainTabSelected: {
        background: '#d7d8fc 0% 0% no-repeat padding-box',
        borderRadius: '5px',
        color: '#4952FF',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
      },
      input: {
        width: '85%',
      },
      toolbarRow: {
        marginLeft: -10,
        width: '100%',
        marginTop: 5,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    // STATE INITIALIZATION
    state = {
      // rule list states
      loading: false,
      loadingTrackers: false,
      reviewOrderBy: ReviewOrderByImpressions,
      updatingReviewOrderby: false,
      updatingReviewStatus: false,
      // queryParams state
      queryParams: new URLSearchParams(this.props.location.search),
      // current tab
      currentTab: 'Brand Safety Rules',
      // delete rule popup
      deletePopupActive: false,
      deletePopupItemID: '',
      deletePopupItemName: '',
      // rules list
      rules: [] as IBrandSafetyRule[],
      // snackbar - used to display small notification
      snackbarMessage: '',
      snackbarError: false,
      // list sorting
      orderDirection: 'asc' as 'asc' | 'desc',
      orderBy: 'Status',
      // search
      searchFilter: '', // user search query
      // pagination
      page: 0, // current page
      rowsPerPage: 10, // number of entries per page
      pageTrackers: 0, // current page
      rowsPerPageTrackers: 10, // number of entries per page
      // permissions
      hasPublisherReadRight:
        notAllowedEmptyIPermissionCheck /* todo uncomment to release global targeting - IS_AUTHORIZED(['read_publisher_brandsafety_rule'], this.props.user.user)*/,
      hasPublisherWriteRight:
        notAllowedEmptyIPermissionCheck /* todo uncomment to release global targeting - IS_AUTHORIZED(['write_publisher_brandsafety_rule'], this.props.user.user)*/,
      hasChannelReadRight: IS_AUTHORIZED(['read_channel_brandsafety_rule'], this.props.user.user),
      hasChannelWriteRight: IS_AUTHORIZED(['write_channel_brandsafety_rule'], this.props.user.user),
      hasBidderReadRight: IS_AUTHORIZED(['read_channel_brandsafety_rule'], this.props.user.user),
      hasBidderWriteRight: IS_AUTHORIZED(['write_channel_brandsafety_rule'], this.props.user.user),
      trackers: [] as IBrandSafetyTracker[],

      // Lists
      siteList: {} as IMenuList,
      bidderList: {} as IMenuList,
      bidderTierList: {} as IMenuList,
      demandSourceList: {} as IMenuList,

      // Filters
      adomainFilter: '',
      categoryFilter: '',
      creativeURLFilter: '',
      reviewStatusFilter: REVIEW_STATUS_UNREVIEWED,
      siteFilter: setStringFromParam(this.props.location.search, 'site_id'),
      bidderFilter: setStringFromParam(this.props.location.search, 'bidder_id'),
      bidderTierFilter: '',
      demandSourceFilter: '',
      openEditCreativeReviewModal: false,
      updatingCreativeReview: false,
      // IAB Category
      IABCategoryPrefix: '',
      IABCategorySuffix: '',
      IABSubCategoryList: {} as IMenuList,
      // Notes Drawer
      openNotesDrawer: false,
      creativeData: {} as ICreativeValidationRowData,
      updatingNotes: false,
      // variables used by debounceGetCreativeValidations
      forceAPIRefresh: false,
    };

    // creativeValidationTableRef is a reference on the MaterialTable component underlying under the ListTable component.
    // It is used to force-refresh the MaterialTable when we changed the data with 'this.channelTableRef.onQueryChange()'.
    creativeValidationReviewedTableRef = React.createRef<any>();
    creativeValidationBlockedTableRef = React.createRef<any>();
    creativeValidationUnreviewedTableRef = React.createRef<any>();
    creativeValidationAllowedTableRef = React.createRef<any>();
    creativeValidationBlockedByAPIRef = React.createRef<any>();

    // variables used by debounceGetCreativeValidations

    // initialization → executed after this component mounts
    componentDidMount() {
      // if rules are defined in the session, load them before doing the query to the API
      this.getRulesFromSession();
      // then make the actual query to the API to refresh changes
      this.getRulesFromAPI();
      // get the trackers
      this.getTrackers();
      // get saved creative_validations
      this.creativeValidationUnreviewedTableRef.current.onQueryChange();
      // For Filters
      this.generateSiteList();
      this.generateBidderAndDemandSourceList();
      this.generateBidderTierList();
    }

    refreshCreativeValidations = () => {
      this.setState(
        {
          updatingReviewStatus: true,
        },
        () => {
          const { reviewStatusFilter } = this.state;
          this.switchCreativeValidationRefs(reviewStatusFilter);
        },
      );
    };

    handelCreativeValidationReviewOrderChange = (id: number) => {
      const { reviewOrderBy, reviewStatusFilter } = this.state;

      // only force api update when user clicks different header (blocked responses or impressions)
      // in blocked creative validation list, there is only blocked responses, no need to force it to update
      // just sort the results in the current list
      if (
        reviewStatusFilter === REVIEW_STATUS_UNREVIEWED &&
        [ReviewOrderByBlockedResponses, ReviewOrderByImpressions].includes(id) &&
        reviewOrderBy !== id
      ) {
        this.setState({
          reviewOrderBy: id,
          updatingReviewOrderby: true,
        });
      }
    };

    getCreativeValidations = (
      query?: any,
    ): Promise<{
      data: ICreativeValidationRowData[];
      page: number;
      totalCount: number;
    }> => {
      return new Promise((resolve) => {
        this.debounceGetCreativeValidations(query ? query : {})
          .then((creativeValidations: ICreativeValidation[]) => {
            const cvsFromSnowflakeData = creativeValidations.map(
              transformCreativeValidationToRowData,
            );
            // resolve the promise
            resolve({
              data: cvsFromSnowflakeData,
              page: query.page,
              totalCount: creativeValidations.length > 0 ? creativeValidations[0].TotalCount : 0,
            });
          })
          .catch(() => {
            this.setState({
              snackbarError: true,
              snackbarMessage:
                'Error while getting the list of creative_validations from the Snowflake.',
            });
          });
      });
    };

    // debounceGetCreativeValidations querychecks if something changed in the query object, and if it did, send the request to the API.
    // If nothing has changed, this function returns the previous cached results without calling the API.
    // The 'forceAPIRefresh' field in this class can be used to force-update the API.
    debounceGetCreativeValidations = (newQuery?: any): Promise<ICreativeValidation[]> => {
      const { siteFilter, bidderFilter, bidderTierFilter, demandSourceFilter } = this.state;

      // query object has changed, or we want to force refresh: query the API
      return new Promise<ICreativeValidation[]>((resolve, reject) => {
        const {
          adomainFilter,
          categoryFilter,
          creativeURLFilter,
          reviewStatusFilter,
          reviewOrderBy,
        } = this.state;

        // call the API
        getCreativeValidationsV2(this.props.session.publisher.ID, reviewStatusFilter, {
          adomain_filter: adomainFilter,
          category_filter: categoryFilter === 'IAB: Empty' ? '' : categoryFilter,
          creative_url_filter: creativeURLFilter,
          order_by: reviewOrderBy,
          page: newQuery ? newQuery.page : 0,
          site_filter: siteFilter,
          bidder_filter: bidderFilter,
          bidder_tier_filter: bidderTierFilter,
          demand_source_filter: demandSourceFilter,
        })
          .then((creativeValidations: ICreativeValidation[]) => {
            // only show the first url
            creativeValidations = creativeValidations.map((validation) => {
              validation.CreativeURL = validation.CreativeURL.split('|||||')[0];
              return validation;
            });

            // resolve promise
            resolve(creativeValidations);
          })
          .then(() => {
            this.setState({
              updatingCreativeReview: false,
              updatingNotes: false,
              updatingReviewStatus: false,
              updatingReviewOrderby: false,
              forceAPIRefresh: false,
            });
          })
          .catch((err: any) => {
            this.setState({
              snackbarError: true,
              snackbarMessage:
                'Error while getting the list of creative_validations from the Snowflake.',
            });
            reject(err);
          });
      });
    };

    toggleReviewStatus = (data: ICreativeValidationRowData, status: number) => {
      this.setState(
        {
          updatingReviewStatus: true,
        },
        () => {
          // if a user clicks on the same status (allowed already, clicks allowed again, move it to unreviewed)
          if (data.ReviewStatus === status) {
            status = REVIEW_STATUS_UNREVIEWED;
          }
          updateCreativeValidation(this.props.session.publisher.ID, {
            OriginalCreativeURL: data.OriginalCreativeURL,
            CreativeURL: data.CreativeURL,
            Adomain: data.Adomain,
            IABCategory: data.IABCategoryCodes,
            ReviewStatus: status,
            Notes: data.Notes,
          } as Partial<ICreativeValidation>)
            .then(() => {
              this.setState(
                {
                  updatingReviewStatus: false,
                  snackbarError: false,
                },
                this.refreshCreativeValidations,
              );
            })
            .catch(() => {
              this.setState({
                updatingReviewStatus: false,
                snackbarError: true,
                snackbarMessage:
                  'Error while updating the review status of the creative validation from the API.',
              });
            });
        },
      );
    };

    // get trackers
    getTrackers = () => {
      getBrandSafetyTrackers(this.props.session.publisher.ID)
        .then((val: IHttpResponse<IBrandSafetyTracker[]>) => {
          this.setState({
            trackers: val.parsedBody as IBrandSafetyTracker[],
          });
        })
        .catch(() => {
          this.setState({
            trackers: [] as IBrandSafetyTracker[],
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of trackers from the API.',
          });
        });
    };

    // get the rules list from the session variable
    getRulesFromSession() {
      if (this.props.session !== undefined && this.props.session.brandSafetyRules !== undefined) {
        this.setState({
          brandSafetyRules: this.props.session.brandSafetyRules,
        });
      }
    }

    // get the rules list from the console API
    getRulesFromAPI() {
      // build request URL
      const reqURL = createUrl('/brandsafetyrules', this.props.session.publisher.ID);
      // make the request
      restGet<IBrandSafetyRule[]>(reqURL, {})
        .then((val: IHttpResponse<IBrandSafetyRule[]>) => {
          const apiRules = val.parsedBody as IBrandSafetyRule[];
          // OK
          this.setState({
            rules: apiRules,
          });
          this.props.session.brandSafetyRules = apiRules;
        })
        .catch(() => {
          // error
          this.setState({
            rules: [] as IBrandSafetyRule[],
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of rules from the API.',
          });
        });
    }

    // update an already existing rule in the API
    // if ruleIndex is defined → update the rule in the state if success
    updateRuleInAPI(rule: IBrandSafetyRule, ruleIndex: number) {
      const rules = [...this.state.rules];
      // build request URL
      const reqURL = createUrl(`/brandsafetyrules/${rule.ID}`, this.props.session.publisher.ID);
      // make the request
      restPatch<IBrandSafetyRule>(reqURL, rule)
        .then(() => {
          // ok → update rule in state
          rules[ruleIndex] = rule;
          this.setState({
            rules,
            snackbarError: false,
            snackbarMessage: `Rule '${rule.Name}' updated`,
          });
        })
        .catch(() => {
          // error
          this.setState({
            snackbarError: true,
            snackbarMessage: `Error while updating rule '${rule.Name}'`,
          });
        });
    }

    // return a promise that deletes a rule or tracker in the API
    deleteItemWithPromise(itemID: number) {
      let reqURL;
      // build request URL
      if (this.isCurrentTab('Brand Safety Rules')) {
        reqURL = createUrl(`/brandsafetyrules/${itemID}`, this.props.session.publisher.ID);
      } else {
        reqURL = createUrl(`/brand_safety_tracker/${itemID}`, this.props.session.publisher.ID);
      }
      return restDelete<IBrandSafetyTracker | IBrandSafetyRule>(reqURL, null);
    }

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        [target.name]: target.value,
      });
    };

    handleChangeIABCat = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const IABSubCategoryList: IMenuList = {};
      Object.keys(IAB_CATEGORIES[target.value].values).forEach((val) => {
        IABSubCategoryList[val] = {
          ID: val,
          title: IAB_CATEGORIES[target.value].values[val],
          selected: false,
        };
      });

      this.setState({
        [target.name]: target.value,
        categoryFilter: target.value,
        IABSubCategoryList,
      });
    };

    handleClear = () => {
      this.setState({
        adomainFilter: '',
        creativeURLFilter: '',
        IABCategoryPrefix: '',
        IABCategorySuffix: '',
        categoryFilter: '',
      });
    };

    handleMenuAcceptClick = (list: IMenuList) => {
      const selectedIAB = [] as string[];
      // find the selected sub-categories and combine them with the IABCategoryPrefix
      Object.keys(list).forEach((value: string) => {
        if (list[value].selected) {
          selectedIAB.push(this.state.IABCategoryPrefix + '-' + list[value].ID);
        }
      });
      this.setState({
        categoryFilter: selectedIAB.length ? selectedIAB.join(';') : this.state.IABCategoryPrefix,
      });
    };

    handleChangeReviewStatusFilter = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const { reviewStatusFilter } = this.state;

      let newReviewOrderBy = ReviewOrderByNone;
      if (target.value !== null && Number(target.value) === REVIEW_STATUS_UNREVIEWED) {
        newReviewOrderBy = ReviewOrderByImpressions;
      } else if (Number(target.value) === REVIEW_STATUS_BLOCKED) {
        newReviewOrderBy = ReviewOrderByBlockedResponses;
      }

      this.setState(
        {
          reviewStatusFilter: Number(target.value),
          reviewOrderBy: newReviewOrderBy,
        },
        () => {
          // only query snowflake table review status has changed
          if (reviewStatusFilter !== Number(target.value)) {
            this.refreshCreativeValidations();
          }
        },
      );
    };

    // handles the rules / tracker filter change (when user change the search text field)
    handleFilterChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState({
        searchFilter: target.value, // change user query
        page: 0, // reset pagination to 0
      });
    };

    // handles the rule's active switch change
    handleActiveSwitchChange = (item: IBrandSafetyRule | IBrandSafetyTracker, checked: boolean) => {
      if (this.isCurrentTab('Brand Safety Rules')) {
        const index = this.state.rules.indexOf(item as IBrandSafetyRule);
        const updatedItem = { ...item } as IBrandSafetyRule;
        updatedItem.Active = checked ? BrandSafetyRuleActiveActive : BrandSafetyRuleActiveInactive;
        this.updateRuleInAPI(updatedItem as IBrandSafetyRule, index);
      } else {
        const index = this.state.trackers.indexOf(item as IBrandSafetyTracker);
        const updatedItem = { ...item } as IBrandSafetyTracker;
        updatedItem.Active = checked
          ? BrandSafetyTrackerActiveActive
          : BrandSafetyTrackerActiveInactive;
        updateBrandSafetyTracker(updatedItem)
          .then((val: IHttpResponse<IBrandSafetyTracker>) => {
            const trackers = this.state.trackers;
            trackers[index] = val.parsedBody as IBrandSafetyTracker;
            this.setState({
              trackers,
              snackbarError: false,
              snackbarMessage: `Tracker '${item.Name}' updated`,
            });
          })
          .catch(() => {
            this.setState({
              snackbarError: true,
              snackbarMessage: `Error while updating tracker '${item.Name}'`,
            });
          });
      }
    };

    handleApplyFilterKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.refreshCreativeValidations();
      }
    };

    // handle sorting
    handleRequestSort = (property: string) => {
      const isAsc = this.state.orderBy === property && this.state.orderDirection === 'asc';
      this.setState({
        orderDirection: isAsc ? 'desc' : 'asc',
        orderBy: property,
      });
    };

    // return the route path to navigate to the brand blocked settings
    getRuleSettingsRoute = (rule: IBrandSafetyRule): string => {
      return getRoutePath(RouteBrandSafetyEdit, extractRouteParams(this.props), {
        brandSafetyRuleID: String(rule.ID),
      });
    };

    // handles click on rule's name → navigates to the edition page for this rule
    editRule = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      this.props.session.brandSafetyReferrer = 'brandsafety-list'; // set referrer for the edit view 'close' button
      navigate(RouteBrandSafetyEdit, extractRouteParams(this.props), {
        brandSafetyRuleID: currentTarget.value,
      });
    };

    // return the route path to navigate to the brand tracker's settings
    getTrackerSettingsRoute = (tracker: IBrandSafetyTracker): string => {
      return getRoutePath(RouteBrandSafetyTrackerEdit, extractRouteParams(this.props), {
        brandSafetyTrackerID: String(tracker.ID),
      });
    };

    // handles click on tracker's name → navigates to the edition page for this tracker
    editTracker = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      navigate(RouteBrandSafetyTrackerEdit, extractRouteParams(this.props), {
        brandSafetyTrackerID: currentTarget.value,
      });
    };

    // handles the deletion of a rule
    showDeleteRulePopup = ({ currentTarget }: React.MouseEvent<HTMLButtonElement>) => {
      const id = currentTarget.value;
      const item = this.findItemByID(Number(id));
      if (item) {
        this.setState({
          deletePopupActive: true,
          deletePopupItemID: String(item.ID),
          deletePopupItemName: item.Name,
        });
      }
    };

    // handles the page change for the rules list pagination
    handleTablePaginationPageChange = (_: any, page: number) => {
      if (this.isCurrentTab('Brand Safety Rules')) {
        this.setState({
          page,
        });
      } else {
        this.setState({
          pageTrackers: page,
        });
      }
    };

    // handle change in number of row per page for table pagination
    handleTablePaginationRowsPerPageChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (this.isCurrentTab('Brand Safety Rules')) {
        this.setState({
          page: 0,
          rowsPerPage: parseInt(event.target.value, 10),
        });
      } else {
        this.setState({
          pageTrackers: 0,
          rowsPerPageTrackers: parseInt(event.target.value, 10),
        });
      }
    };

    // handle the rule entries filtering related to the user search query
    filterRules = (rules: IBrandSafetyRule[]) => {
      return rules.filter((rule: IBrandSafetyRule) => {
        return !(
          !rule.Name.toLowerCase().includes(this.state.searchFilter.toLowerCase()) &&
          !rule.ID.toString().includes(this.state.searchFilter)
        );
      });
    };

    // handle the tracker entries filtering related to the user search query
    filterTrackers = (trackers: IBrandSafetyTracker[]) => {
      return trackers.filter((tracker: IBrandSafetyTracker) => {
        return !(
          !tracker.Name.toLowerCase().includes(this.state.searchFilter.toLowerCase()) &&
          !tracker.ID.toString().includes(this.state.searchFilter)
        );
      });
    };

    // '+ Add Rule' button click handler
    addRuleButtonClickHandler = () => {
      navigate(
        RouteBrandSafetyNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // '+ Add Tracker' button click handler
    addTrackerButtonClickHandler = () => {
      navigate(
        RouteBrandSafetyTrackerNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    // function that handle the hiding of the delete popup
    hideDeleteDialog = () => {
      this.setState({
        deletePopupActive: false,
      });
    };

    // function called when the item has been deleted with success (after delete popup)
    // delete the tracker or rule locally in the state tracker or rule list
    onItemDelete = (itemIDStr: string) => {
      const itemID = Number(itemIDStr);
      let itemIndex;
      // find index of rule in state rule array
      if (this.isCurrentTab('Brand Safety Rules')) {
        itemIndex = this.state.rules.findIndex((rule: IBrandSafetyRule) => rule.ID === itemID);
        // delete item at index in array
        this.setState({
          rules: update(this.state.rules, { $splice: [[itemIndex, 1]] }),
          deletePopupActive: false,
        });
      } else {
        itemIndex = this.state.trackers.findIndex(
          (tracker: IBrandSafetyTracker) => tracker.ID === itemID,
        );
        // delete item at index in array
        this.setState({
          trackers: update(this.state.trackers, { $splice: [[itemIndex, 1]] }),
          deletePopupActive: false,
        });
      }
    };

    // function called when a snackbar notification is closed
    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    // returns a string describing the channel/bidder targeting of a rule
    GetRuleTargetingString(rule: IBrandSafetyRule): string {
      // handle global
      if (rule.RuleTargetingType === BrandSafetyRuleTargetingTypeGlobal) {
        return 'Global';
      }
      // write message
      let message = `${rule.TargetedObjects.length} ${targetingTypeToString(
        rule.RuleTargetingType,
      )}`;
      if (rule.TargetedObjects.length > 1) {
        message += 's';
      }
      // add excluded if the results are excluded
      if (rule.IsTargetingExcluded) {
        message += ' excluded';
      }
      return message;
    }

    // returns a string containing the rule type (IAB or Adomain or Mediafile}
    GetRuleTypeString(
      rule: IBrandSafetyRule,
    ): 'IAB' | 'Adomain' | 'Mediafile' | 'CreativeID' | 'Undefined' {
      if (rule.TargetedIABs) {
        return 'IAB';
      }
      if (rule.TargetedAdomains) {
        return 'Adomain';
      }
      if (rule.TargetedMediafiles) {
        return 'Mediafile';
      }
      if (rule.TargetedCreativeIDs) {
        return 'CreativeID';
      }
      return 'Undefined';
    }

    // returns the item that correspond to the given ID
    findItemByID(itemID: number): IBrandSafetyRule | IBrandSafetyTracker | undefined {
      if (this.isCurrentTab('Brand Safety Rules')) {
        return this.state.rules.find((rule: IBrandSafetyRule) => rule.ID === itemID);
      } else {
        return this.state.trackers.find((tracker: IBrandSafetyTracker) => tracker.ID === itemID);
      }
    }

    // sort the rule list accordingly to what the user choose on the table
    sortRuleList(ruleList: IBrandSafetyRule[]) {
      ruleList.sort((a: IBrandSafetyRule, b: IBrandSafetyRule) => {
        // get rule targeting
        const aTargetingStr = this.GetRuleTargetingString(a);
        const bTargetingStr = this.GetRuleTargetingString(b);
        // get rule type
        const aTypeStr = this.GetRuleTypeString(a);
        const bTypeStr = this.GetRuleTypeString(b);
        switch (this.state.orderBy) {
          case 'IsActive':
            return (this.state.orderDirection === 'asc' ? -1 : 1) * (a.Active - b.Active);
          case 'Name':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.Name === b.Name ? 0 : a.Name > b.Name ? 1 : -1)
            );
          case 'Type':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (aTypeStr === bTypeStr ? 0 : aTypeStr > bTypeStr ? 1 : -1)
            );
          case 'Targeting':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (aTargetingStr === bTargetingStr ? 0 : aTargetingStr > bTargetingStr ? 1 : -1)
            );
          case 'NbrResponseBlocked':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.NbrResponseBlocked - b.NbrResponseBlocked)
            );
          default:
            return 0; // don't sort
        }
      });
      return ruleList;
    }

    // sort the tracker list accordingly to what the user choose on the table
    sortTrackerList(trackerList: IBrandSafetyTracker[]) {
      trackerList.sort((a: IBrandSafetyTracker, b: IBrandSafetyTracker) => {
        // get rule targeting
        const aTargetingStr = getTrackerEventTargeting(a);
        const bTargetingStr = getTrackerEventTargeting(b);
        switch (this.state.orderBy) {
          case 'Active':
            return (this.state.orderDirection === 'asc' ? -1 : 1) * (a.Active - b.Active);
          case 'Name':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.Name === b.Name ? 0 : a.Name > b.Name ? 1 : -1)
            );
          case 'ThirdParty':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.ThirdParty === b.ThirdParty ? 0 : a.ThirdParty > b.ThirdParty ? 1 : -1)
            );
          case 'Events':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (aTargetingStr === bTargetingStr ? 0 : aTargetingStr > bTargetingStr ? 1 : -1)
            );
          case 'MeasuredInventory':
            return (
              (this.state.orderDirection === 'asc' ? 1 : -1) *
              (a.MeasuredInventory - b.MeasuredInventory)
            );
          default:
            return 0; // don't sort
        }
      });
      return trackerList;
    }

    isRuleModificationAllowed(rule: IBrandSafetyRule): boolean {
      switch (rule.RuleTargetingType) {
        case BrandSafetyRuleTargetingTypeGlobal:
          return this.state.hasPublisherWriteRight.isAuthorized;
        case BrandSafetyRuleTargetingTypeChannel:
        case BrandSafetyRuleTargetingTypeChannelGroup:
          return this.state.hasChannelWriteRight.isAuthorized;
        case BrandSafetyRuleTargetingTypeBidder:
        case BrandSafetyRuleTargetingTypeBidderGroup:
          return this.state.hasBidderWriteRight.isAuthorized;
      }
      return false;
    }

    // ############################## Generate Lists ##############################

    generateSiteList = () => {
      getSitesPromise({
        publisherIDs: [this.props.session.publisher.ID],
        publisherID: this.props.session.publisher.ID,
      })
        .then((sites: ISite[]) => {
          const presetSiteID = this.state.queryParams.get('site_id');
          // generate site list
          const siteList: IMenuList = {};
          filterSiteByPrebid(sites).forEach((site: ISite) => {
            const siteName = checkUserInfoMasked(this.props.user.user)
              ? `demo_site_${site.ID}`
              : site.Name;
            siteList[site.ID] = {
              title: siteName,
              selected: presetSiteID === `${site.ID}` ? true : false,
              ID: site.ID.toString(),
            };
          });
          this.setState({
            siteList,
          });
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of sites from the Snowflake.',
          });
        });
    };

    generateBidderAndDemandSourceList = () => {
      this.setState(
        {
          bidderListIsLoading: true,
        },
        () => {
          queryHeaderBidders({
            query: {
              publisher_id: this.props.session.publisher.ID,
              include_deleted: false,
            },
          })
            .then((headerbidders: IHeaderBidder[]) => {
              const presetBidderID = this.state.queryParams.get('bidder_id');
              const bidderList: IMenuList = {};
              headerbidders.forEach((bidder: IHeaderBidder) => {
                bidderList[bidder.ID] = {
                  title: checkUserInfoMasked(this.props.user.user)
                    ? `demo_bidder_${bidder.ID}`
                    : bidder.Name,
                  selected: presetBidderID === bidder.ID ? true : false,
                  ID: bidder.ID,
                };
              });
              this.setState(
                {
                  bidderList,
                  bidderListIsLoading: false,
                },
                () => {
                  this.generateDemandSourceList(headerbidders);
                },
              );
            })
            .catch(() => {
              this.setState({
                snackbarError: true,
                snackbarMessage:
                  'Error while getting the list of headerbidders from the Snowflake.',
              });
            });
        },
      );
    };

    generateBidderTierList = () => {
      const bidderTierList: IMenuList = {};
      [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].forEach((tier: number) => {
        bidderTierList[tier] = {
          title: `Tier ${tier}`,
          selected: false,
          ID: `${tier}`,
        };
      });
      this.setState({
        bidderTierList,
      });
    };

    // generate dropdown demand source list for bid saver
    generateDemandSourceList = (bidders: IHeaderBidder[]) => {
      const demandSourceList: IMenuList = genBiddersMenuList(
        [],
        this.props.session.publisher,
        bidders,
        '',
        hasRoleSuperUser(this.props.user.user),
      );
      this.setState({
        demandSourceList,
      });
    };

    // ############################## Generate Filters ##############################

    generateFilter = (list: IMenuList, type: string) => {
      const array = FindSelectedIDsFromMenuList(list);
      const filter = array.join(',');
      this.setState(
        {
          [`${type}Filter`]: filter,
        },
        () => {
          this.refreshCreativeValidations();
        },
      );
    };

    // ############################## Render Filters ##############################

    renderSiteFilter = () => {
      const { siteList } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Site</Typography>
            </Grid>
            <Grid item xs={9}>
              <MaterialSearchDropdownMulti
                width='150px'
                fontSize='12px'
                menuTitle=''
                list={siteList}
                onChange={(list: IMenuList) => {
                  this.setState({
                    siteList: list,
                  });
                }}
                disabled={this.state.reviewStatusFilter !== REVIEW_STATUS_UNREVIEWED}
                handleMenuAcceptClick={(src: IMenuList) => this.generateFilter(src, 'site')}
                menuTitleOverride={'Select Channels'}
                selectLimit={-1}
                forceOpen={false}
                onlyShowNumberWhenClosed={true}
                maxHeight={350}
                enableFixed={true}
                selectAll={false}
                showPrefixID
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderBidderFilter = () => {
      const { bidderList } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Bidder</Typography>
            </Grid>
            <Grid item xs={9}>
              <MaterialSearchDropdownMulti
                width='150px'
                fontSize='12px'
                menuTitle=''
                list={bidderList}
                onChange={(list: IMenuList) => {
                  this.setState({
                    bidderList: list,
                  });
                }}
                disabled={this.state.reviewStatusFilter !== REVIEW_STATUS_UNREVIEWED}
                handleMenuAcceptClick={(src: IMenuList) => this.generateFilter(src, 'bidder')}
                menuTitleOverride={'Select Bidders'}
                selectLimit={-1}
                forceOpen={false}
                onlyShowNumberWhenClosed={true}
                maxHeight={350}
                enableFixed={true}
                selectAll={false}
                showPrefixID
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderBidderTierFilter = () => {
      const { bidderTierList } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Bidder Tier</Typography>
            </Grid>
            <Grid item xs={9}>
              <MaterialSearchDropdownMulti
                width='150px'
                fontSize='12px'
                menuTitle=''
                list={bidderTierList}
                onChange={(list: IMenuList) => {
                  this.setState({
                    bidderTierList: list,
                  });
                }}
                disabled={this.state.reviewStatusFilter !== REVIEW_STATUS_UNREVIEWED}
                handleMenuAcceptClick={(src: IMenuList) => this.generateFilter(src, 'bidderTier')}
                menuTitleOverride={'Select BidderTier'}
                selectLimit={-1}
                forceOpen={false}
                onlyShowNumberWhenClosed={true}
                maxHeight={350}
                enableFixed={true}
                selectAll={false}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderDemandSourceFilter = () => {
      const { demandSourceList } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Demand Source</Typography>
            </Grid>
            <Grid item xs={9}>
              <MaterialSearchDropdownMulti
                width='150px'
                fontSize='12px'
                menuTitle=''
                list={demandSourceList}
                onChange={(list: IMenuList) => {
                  this.setState({
                    demandSourceList: list,
                  });
                }}
                disabled={this.state.reviewStatusFilter !== REVIEW_STATUS_UNREVIEWED}
                handleMenuAcceptClick={(src: IMenuList) => this.generateFilter(src, 'demandSource')}
                menuTitleOverride={'Select Demand Source'}
                selectLimit={-1}
                forceOpen={false}
                onlyShowNumberWhenClosed={true}
                maxHeight={350}
                enableFixed={true}
                selectAll={false}
              />
            </Grid>
          </Grid>
        </Grid>
      );
    };

    // ############################## Download APIs ##############################
    genDownloadCreativeValidationCSV = () => {
      const {
        adomainFilter,
        categoryFilter,
        creativeURLFilter,
        siteFilter,
        bidderFilter,
        bidderTierFilter,
        demandSourceFilter,
      } = this.state;

      let reviewStatus = 'unreviewed';
      if (this.state.reviewStatusFilter === REVIEW_STATUS_REVIEWED) {
        reviewStatus = 'reviewed';
      } else if (this.state.reviewStatusFilter === REVIEW_STATUS_BLOCKED) {
        reviewStatus = 'blocked';
      } else if (this.state.reviewStatusFilter === REVIEW_STATUS_ALLOWED) {
        reviewStatus = 'allowed';
      } else if (this.state.reviewStatusFilter === REVIEW_STATUS_BLOCKED_BY_API) {
        reviewStatus = 'blocked_by_api';
      }
      const reqURL = addDomainAndTokenToUrl(
        `/publishers/${this.props.session.publisher.ID}/download_creative_validation_${reviewStatus}`,
      );
      const reqUrlWithParams = addQueryParams(reqURL, {
        adomain_filter: adomainFilter,
        category_filter: categoryFilter,
        creative_url_filter: creativeURLFilter,
        site_filter: siteFilter,
        bidder_filter: bidderFilter,
        bidder_tier_filter: bidderTierFilter,
        demand_source_filter: demandSourceFilter,
      });
      return reqUrlWithParams;
    };

    // RENDERING FUNCTIONS

    // ############################## RENDER LIST ROWS WHEN EMPTY ##############################
    renderEmptyRulesList() {
      return (
        <TableRow>
          <TableCell colSpan={4}>No Data available</TableCell>
        </TableRow>
      );
    }

    // ############################## RENDER LIST ROWS WHEN LOADING ##############################
    renderLoadingRulesList() {
      return (
        <TableRow>
          <TableCell colSpan={4}>Loading...</TableCell>
        </TableRow>
      );
    }

    // ############################## RENDER LIST ROWS WHEN FILLED ##############################
    renderFilledRulesList() {
      // rules filtering → filter rules according to the user 'search' input
      const filteredRules = this.filterRules(this.state.rules);
      // rules sorting → sort rules according to what the user choose to sort in the rules list component
      const sortedRules = this.sortRuleList(filteredRules);
      // rules pagination
      const paginatedRules = sortedRules.slice(
        this.state.page * this.state.rowsPerPage,
        (this.state.page + 1) * this.state.rowsPerPage,
      );

      // display only rules that permissions allow to
      const displayedRules = [] as IBrandSafetyRule[];
      paginatedRules.forEach((rule: IBrandSafetyRule) => {
        // global
        if (
          this.state.hasPublisherReadRight.isAuthorized &&
          rule.RuleTargetingType === BrandSafetyRuleTargetingTypeGlobal
        ) {
          displayedRules.push(rule);
        }
        // channel
        if (
          this.state.hasChannelReadRight.isAuthorized &&
          (rule.RuleTargetingType === BrandSafetyRuleTargetingTypeChannel ||
            rule.RuleTargetingType === BrandSafetyRuleTargetingTypeChannelGroup)
        ) {
          displayedRules.push(rule);
        }
        // bidder
        if (
          this.state.hasBidderReadRight.isAuthorized &&
          (rule.RuleTargetingType === BrandSafetyRuleTargetingTypeBidder ||
            rule.RuleTargetingType === BrandSafetyRuleTargetingTypeBidderGroup)
        ) {
          displayedRules.push(rule);
        }
      });

      // actual rendering of rows
      return displayedRules.map((row: IBrandSafetyRule) => {
        // get the type chip for this rule
        return (
          <TableRow key={'r_' + row.ID}>
            {/* ACTIVE / INACTIVE */}
            <TableCell align='center'>
              <div>
                <Switch
                  disabled={!this.isRuleModificationAllowed(row)}
                  checked={row.Active === BrandSafetyRuleActiveActive}
                  onChange={({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
                    this.handleActiveSwitchChange(row, currentTarget.checked);
                  }}
                  color='primary'
                />
              </div>
            </TableCell>

            {/* NAME */}
            <TableCell align='center'>
              {/* button that handle click on rule's name to go to its settings */}
              <Button component={Link} to={this.getRuleSettingsRoute(row)}>
                <div
                  style={{
                    textAlign: 'left',
                  }}
                >
                  {/* display rule name */}
                  <strong>
                    {checkUserInfoMasked(this.props.user.user) ? 'demo_rule_' + row.ID : row.Name}
                  </strong>
                </div>
              </Button>
            </TableCell>

            {/* TYPE CHIP */}
            <TableCell align='center'>{this.renderTypeChip(row)}</TableCell>

            {/* TARGETING */}
            <TableCell align='center'>{this.GetRuleTargetingString(row)}</TableCell>

            {/* RESPONSE BLOCKED */}
            <TableCell align='center'>{row.NbrResponseBlocked}</TableCell>

            {/* DELETE */}
            <TableCell align='center'>
              <Button
                disabled={!this.isRuleModificationAllowed(row)}
                onClick={this.showDeleteRulePopup}
                value={row.ID}
              >
                <DeleteIcon
                  style={{
                    fill: this.isRuleModificationAllowed(row) ? deleteColor : '#d9d9d9',
                  }}
                />
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    // ############################## RENDER LIST ROWS WHEN FILLED ##############################
    renderFilledTrackersList() {
      // trackers filtering → filter trackers according to the user 'search' input
      const filteredTrackers = this.filterTrackers(this.state.trackers);
      // trackers sorting → sort trackers according to what the user choose to sort in the trackers list component
      const sortedTrackers = this.sortTrackerList(filteredTrackers);
      // trackers pagination
      const paginatedTrackers = sortedTrackers.slice(
        this.state.pageTrackers * this.state.rowsPerPageTrackers,
        (this.state.pageTrackers + 1) * this.state.rowsPerPageTrackers,
      );

      // actual rendering of rows
      return paginatedTrackers.map((row: IBrandSafetyTracker) => {
        const eventsString = getTrackerEventTargeting(row);
        return (
          <TableRow key={'r_' + row.ID}>
            {/* ACTIVE / INACTIVE */}
            <TableCell align='center'>
              <div>
                <Switch
                  checked={row.Active === BrandSafetyTrackerActiveActive}
                  onChange={({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
                    this.handleActiveSwitchChange(row, currentTarget.checked);
                  }}
                  color='primary'
                />
              </div>
            </TableCell>

            {/* NAME */}
            <TableCell align='center'>
              {/* button that handle click on tracker's name to go to its settings */}
              <Button component={Link} to={this.getTrackerSettingsRoute(row)}>
                <div
                  style={{
                    textAlign: 'left',
                  }}
                >
                  {/* display tracker name */}
                  <strong>
                    {checkUserInfoMasked(this.props.user.user)
                      ? 'demo_tracker_' + row.ID
                      : row.Name}
                  </strong>
                </div>
              </Button>
            </TableCell>

            {/* THIRD PARTY */}
            <TableCell align='center'>{getTrackerThirdParty(row)}</TableCell>

            {/* EVENTS */}
            <TableCell align='center'>{eventsString}</TableCell>

            {/* SCALE / Measured Inventory */}
            <TableCell align='center'>{row.MeasuredInventory}</TableCell>

            {/* DELETE */}
            <TableCell align='center'>
              <Button onClick={this.showDeleteRulePopup} value={row.ID}>
                <DeleteIcon
                  style={{
                    fill: deleteColor,
                  }}
                />
              </Button>
            </TableCell>
          </TableRow>
        );
      });
    }

    // ############################## RENDER EACH ROWS TYPE CHIP ##############################
    renderTypeChip(rule: IBrandSafetyRule) {
      const chip = getRuleTypeChip(rule);
      const ChipComponent = chip.component;
      return <ChipComponent label={chip.label} style={{ alignItems: 'right' }} />;
    }

    // ############################## RENDER LIST ROWS ##############################
    renderListRows() {
      if (this.isCurrentTab('Brand Safety Rules')) {
        // if loading
        if (this.state.loading) {
          return this.renderLoadingRulesList();
        }
        // if empty
        if (this.state.rules.length === 0) {
          return this.renderEmptyRulesList();
        }
        // if not empty
        return this.renderFilledRulesList();
      } else {
        // if loading
        if (this.state.loadingTrackers) {
          return this.renderLoadingRulesList();
        }
        // if empty
        if (this.state.trackers.length === 0) {
          return this.renderEmptyRulesList();
        }
      }
      // if not empty
      return this.renderFilledTrackersList();
    }

    // ************************************
    // ********* TABLE FUNCTIONS **********
    // ************************************

    // isCurrentTab receives one argument and compares that against the `currentTab` in state
    isCurrentTab = (tabToCheck: string): boolean => {
      const { currentTab } = this.state;
      return currentTab === tabToCheck;
    };

    // setSettingsTabsCurrentTab is passed to the SettingsTabs component to set the current tab
    setSettingsTabsCurrentTab = (val: number) => {
      let currentTab: string;
      switch (val) {
        case 1:
          currentTab = 'Event Trackers';
          break;
        case 2:
          currentTab = 'Creative Review';
          break;
        default:
          currentTab = 'Brand Safety Rules';
      }
      this.setState({
        currentTab,
      });
    };

    // ************************************
    // ********* RENDER FUNCTIONS *********
    // ************************************

    // renderTabs renders the `All` and `Groups` tabs for the Channels view
    // depending on the value of `currentTab` stored in the state
    renderTabs() {
      const tabs: ITab[] = [
        {
          label: 'Brand Safety Rules',
          render: this.renderColumns(rulesListColumns, 'Search rule by name'),
        },
        {
          label: 'Event Trackers',
          render: this.renderColumns(trackerListColumns, 'Search tracker by name'),
        },
        {
          label: 'Creative Review',
          render: this.renderCreativeReview,
        },
      ];
      const hasPresetID =
        !!this.state.queryParams.get('site_id') || !!this.state.queryParams.get('bidder_id');

      return (
        <SettingsTabs
          tabs={tabs}
          setSettingsTabsCurrentTab={this.setSettingsTabsCurrentTab}
          marginTop={10}
          marginBottom={10}
          paddingRight={0}
          startingTab={hasPresetID ? 2 : 0}
        />
      );
    }

    renderColumns(columns: IHeadCell[], placeholder: string) {
      return () => {
        const { classes } = this.props;
        const filteredRules = this.filterRules(this.state.rules);
        const filteredRulesLength = Array.isArray(filteredRules) ? filteredRules.length : 0;
        const filteredTrackers = this.filterTrackers(this.state.trackers);
        const filteredTrackersLength = Array.isArray(filteredTrackers)
          ? filteredTrackers.length
          : 0;
        return (
          <div style={{ paddingBottom: spacing.large }}>
            <Panel paper verticalPadding style={{ width: '100%', overflowX: 'scroll' }}>
              <div>
                <Grid
                  container
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  style={{ padding: '0px 16px' }}
                >
                  <Grid item>
                    {/* SEARCH ENTRY */}
                    <div
                      style={{
                        backgroundColor: '#F8F8F8',
                        display: 'inline-block',
                        marginBottom: '10px',
                        minWidth: 350,
                        marginTop: 10,
                      }}
                    >
                      <IconButton classes={{ root: classes.iconButton }}>
                        <SearchIcon />
                      </IconButton>
                      <InputBase
                        classes={{ root: classes.input }}
                        placeholder={placeholder}
                        name={'searchFilter'} // MUST be the name of the STATE variable
                        value={this.state.searchFilter} // user search query
                        onChange={this.handleFilterChange} // handle filter change
                      />
                    </div>
                  </Grid>
                  <Grid item>
                    {/* BUTTON - '+ Add Rule / Tracker' */}
                    <Grid item>
                      <RowButton
                        right={
                          <ModelMediumButton
                            disabled={
                              !this.state.hasPublisherWriteRight.isAuthorized &&
                              !this.state.hasChannelWriteRight.isAuthorized &&
                              !this.state.hasBidderWriteRight.isAuthorized
                            }
                            variant='contained'
                            color='primary'
                            style={{ paddingLeft: '30px', paddingRight: '30px', width: 170 }}
                            onClick={
                              this.isCurrentTab('Brand Safety Rules')
                                ? this.addRuleButtonClickHandler
                                : this.addTrackerButtonClickHandler
                            }
                          >
                            {this.isCurrentTab('Brand Safety Rules')
                              ? '+ Add Rule'
                              : '+ Add Tracker'}
                          </ModelMediumButton>
                        }
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* RULES LIST */}
                <Table className={classes.table}>
                  {/* LIST COLUMN'S TITLE */}
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === 'name' ? 'left' : 'center'}
                          sortDirection={
                            this.state.orderBy === column.id ? this.state.orderDirection : false
                          }
                        >
                          <TableSortLabel
                            active={this.state.orderBy === column.id}
                            direction={
                              this.state.orderBy === column.id ? this.state.orderDirection : 'asc'
                            }
                            onClick={() => this.handleRequestSort(column.id)}
                            style={{ textAlign: 'center' }}
                          >
                            {/* margin left to compensate the sort label*/}
                            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                            {/* actual title display */}
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      {/* delete button column */}
                      <TableCell align='center'>Delete</TableCell>
                    </TableRow>
                  </TableHead>

                  {/* LIST ROWS */}
                  <TableBody>{this.renderListRows()}</TableBody>
                </Table>

                {/* LIST PAGINATION */}
                {/* @ts-ignore */}
                <TablePagination
                  component='div'
                  count={
                    this.isCurrentTab('Brand Safety Rules')
                      ? filteredRulesLength
                      : filteredTrackersLength
                  }
                  labelDisplayedRows={({ from, to, count }) =>
                    `${count !== 0 ? from : 1}-${to} of ${count}`
                  }
                  page={
                    this.isCurrentTab('Brand Safety Rules')
                      ? this.state.page
                      : this.state.pageTrackers
                  }
                  onChangePage={this.handleTablePaginationPageChange}
                  rowsPerPage={
                    this.isCurrentTab('Brand Safety Rules')
                      ? this.state.rowsPerPage
                      : this.state.rowsPerPageTrackers
                  }
                  onChangeRowsPerPage={this.handleTablePaginationRowsPerPageChange}
                />
              </div>
            </Panel>
          </div>
        );
      };
    }

    // customize the toolbar of creative_validation list table (currently just the filter)
    renderListViewToolbar = () => {
      // get props values
      const { classes } = this.props;

      // renderer
      return (
        <>
          <Cols
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Col>{this.renderFilterBlock()}</Col>
            <Col paddingTop='12px'>
              <IconButton
                classes={{ root: classes.iconButton }}
                style={{ borderRadius: '5px' }}
                target='_blank'
                rel='noopener'
                href={this.genDownloadCreativeValidationCSV()}
              >
                <img src='/assets/shared/svg/DownloadIcon.svg' />
              </IconButton>
            </Col>
          </Cols>
          <Divider
            style={{
              width: '100%',
            }}
          />
          <Grid style={{ margin: '10px 0px', width: '100%' }}>{this.renderFilterButton()}</Grid>
          <Divider style={{ width: '100%' }} />
        </>
      );
    };

    // renderFilterMenuTitle renders the 'Filter' button at the top of the ListTable component.
    renderFilterMenuTitle = () => {
      return (
        <Cols style={{ alignItems: 'center' }}>
          {/* text */}
          <Col style={{ paddingRight: '8px' }}>
            <Typography
              style={{
                marginLeft: -5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'black',
              }}
            >
              FILTERS
            </Typography>
          </Col>
          {/* logo */}
          <Col>
            <img src='/assets/shared/svg/Filter15px.svg' />
          </Col>
        </Cols>
      );
    };

    renderFilterBlock = () => {
      return (
        <Cols>
          <Col
            style={{
              paddingTop: '20px',
              paddingRight: spacing.extraLarge,
            }}
          >
            <Typography
              style={{
                fontSize: 16,
                fontWeight: 500,
                color: 'black',
              }}
            >
              Search
            </Typography>
          </Col>
          <Col>
            <Cols
              wrap
              style={{
                alignItems: 'center',
              }}
            >
              <Col
                style={{
                  paddingRight: spacing.medium,
                  paddingBottom: spacing.medium,
                  width: '160px',
                }}
              >
                <TextField
                  fullWidth
                  label='Creative URL'
                  name='creativeURLFilter'
                  value={this.state.creativeURLFilter}
                  onChange={this.handleChange}
                  onKeyDown={this.handleApplyFilterKeyDown}
                />
              </Col>
              <Col
                style={{
                  paddingRight: spacing.medium,
                  paddingBottom: spacing.medium,
                  width: '160px',
                }}
              >
                <TextField
                  fullWidth
                  label='Adomain or "None"'
                  name='adomainFilter'
                  value={this.state.adomainFilter}
                  onChange={this.handleChange}
                  onKeyDown={this.handleApplyFilterKeyDown}
                />
              </Col>
              {/* IAB Category Prefix */}
              <Col
                style={{
                  paddingRight: spacing.medium,
                  paddingBottom: spacing.medium,
                  width: '160px',
                }}
              >
                <TextField
                  select
                  name='IABCategoryPrefix'
                  className='pb-campaign-form-view_iab_cat'
                  label='IAB Category'
                  value={this.state.IABCategoryPrefix}
                  onChange={this.handleChangeIABCat}
                  onKeyDown={this.handleApplyFilterKeyDown}
                  fullWidth
                >
                  {getAllIABCategories().map((val) => {
                    return (
                      <MenuItem value={val}>
                        {val}: {IAB_CATEGORIES[val].label}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Col>
              {/* IAB Category Suffix */}
              <Col
                style={{
                  paddingTop: '16px',
                  paddingRight: spacing.medium,
                  paddingBottom: spacing.medium,
                  width: '160px',
                }}
              >
                <MaterialSearchDropdownMulti
                  width='150px'
                  fontSize='12px'
                  menuTitle='IAB Sub Category'
                  list={this.state.IABSubCategoryList}
                  onChange={(list: IMenuList) => {
                    this.setState({
                      IABSubCategoryList: list,
                    });
                  }}
                  disabled={this.state.IABCategoryPrefix === ''}
                  handleMenuAcceptClick={(list: IMenuList) => {
                    this.handleMenuAcceptClick(list);
                  }}
                  menuTitleOverride={'Select Sub Categories'}
                  overrideDefaultSortMode={SortByMode.IdAsc}
                  selectLimit={-1}
                  forceOpen={false}
                  maxHeight={350}
                  enableFixed={true}
                  selectAll={false}
                  showPrefixID
                />
              </Col>
              <Col
                style={{
                  paddingRight: spacing.medium,
                  paddingBottom: spacing.medium,
                }}
              >
                <ModelSecondaryButton onClick={this.refreshCreativeValidations}>
                  Apply
                </ModelSecondaryButton>
              </Col>
              <Col
                style={{
                  paddingRight: spacing.medium,
                  paddingBottom: spacing.medium,
                }}
              >
                <ModelSecondaryButton onClick={this.handleClear} style={{ color: '#434343' }}>
                  Clear
                </ModelSecondaryButton>
              </Col>
            </Cols>
          </Col>
        </Cols>
      );
    };

    renderFilterButton = () => {
      // dropdown for the 'Status' filter
      const statusDropdown = {
        closeOnClick: false,
        render: this.renderReviewStatusDropdown,
      };
      // dropdown for the 'Status' filter
      const siteDropdown = {
        closeOnClick: false,
        render: this.renderSiteFilter,
      };
      // dropdown for the 'Status' filter
      const bidderDropdown = {
        closeOnClick: false,
        render: this.renderBidderFilter,
      };
      // dropdown for the 'Status' filter
      const bidderTierDropdown = {
        closeOnClick: false,
        render: this.renderBidderTierFilter,
      };
      // dropdown for the 'Status' filter
      const demandSourceDropdown = {
        closeOnClick: false,
        render: this.renderDemandSourceFilter,
      };

      // definition of all the filters available
      const filterItems = [
        statusDropdown,
        siteDropdown,
        bidderDropdown,
        bidderTierDropdown,
        demandSourceDropdown,
      ];

      // render filters
      return (
        <Grid
          onClick={() => {
            this.setState({ forceFilterMenuButtonClosed: false });
          }}
        >
          <MenuButton
            titleRender={this.renderFilterMenuTitle}
            items={filterItems}
            minWidth={450}
            marginTop={60}
            marginLeft={0}
          />
        </Grid>
      );
    };

    // renderReviewStatusDropdown renders the dropdown for the 'Review Status' filter.
    renderReviewStatusDropdown = () => {
      // get variables from the state
      const { reviewStatusFilter } = this.state;

      // renderer
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Review Status</Typography>
            </Grid>
            <Grid item xs={9}>
              <Select
                name='reviewStatusFilter'
                value={reviewStatusFilter}
                onChange={this.handleChangeReviewStatusFilter}
                style={{ fontSize: '14px' }}
              >
                <MenuItem value={REVIEW_STATUS_UNREVIEWED}>Unreviewed</MenuItem>
                <MenuItem value={REVIEW_STATUS_REVIEWED}>Reviewed</MenuItem>
                <MenuItem value={REVIEW_STATUS_ALLOWED}>Allowed</MenuItem>
                <MenuItem value={REVIEW_STATUS_BLOCKED}>Blocked</MenuItem>
                {(this.props.session.publisher.ID === '381' ||
                  hasRoleSuperUser(this.props.user.user)) && (
                  <MenuItem value={REVIEW_STATUS_BLOCKED_BY_API}>Blocked by API</MenuItem>
                )}
              </Select>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderCreativeURLAction(rowData: ICreativeValidationRowData) {
      const { updatingReviewStatus } = this.state;
      const { classes } = this.props;
      return (
        <Cols
          wrap
          style={{
            alignContent: 'center',
          }}
        >
          <Col>
            <Tooltip
              title={
                <Grid>
                  <Typography className={classes.tooltipBold}>
                    {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED
                      ? 'Marked As Reviewed'
                      : 'Mark As Reviewed'}
                  </Typography>
                  <Typography className={classes.tooltipSmall}>
                    {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED
                      ? 'Click to unreview ad'
                      : 'Ad is ok to run under current rules'}
                  </Typography>
                </Grid>
              }
            >
              <ModelSecondaryButton
                style={{
                  minWidth: '0',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => {
                  if (!updatingReviewStatus) {
                    this.toggleReviewStatus(rowData, REVIEW_STATUS_REVIEWED);
                  }
                }}
              >
                {/* Reviewed check box */}
                {rowData.ReviewStatus === REVIEW_STATUS_REVIEWED ||
                rowData.ReviewStatus === REVIEW_STATUS_UNREVIEWED ? (
                  <img src={'/assets/shared/svg/Review.svg'} width='24px' height='24px' />
                ) : (
                  <img src={'/assets/shared/svg/ReviewDisabled.svg'} width='24px' height='24px' />
                )}
              </ModelSecondaryButton>
            </Tooltip>
          </Col>
          <Col>
            <Tooltip
              title={
                <Grid>
                  <Typography className={classes.tooltipBold}>
                    {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED
                      ? 'Allowed Creative'
                      : 'Allow Creative'}
                  </Typography>
                  <Typography className={classes.tooltipSmall}>
                    {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED
                      ? 'Click to unreview ad'
                      : 'Create an exception for this ad and bypass all brand safety rules'}
                  </Typography>
                </Grid>
              }
            >
              <ModelSecondaryButton
                style={{
                  minWidth: '0',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => {
                  if (!updatingReviewStatus) {
                    this.toggleReviewStatus(rowData, REVIEW_STATUS_ALLOWED);
                  }
                }}
              >
                {/* Allowed Unlocked Icon */}
                {rowData.ReviewStatus === REVIEW_STATUS_ALLOWED ||
                rowData.ReviewStatus === REVIEW_STATUS_UNREVIEWED ? (
                  <img src={'/assets/shared/svg/Allow.svg'} width='24px' height='24px' />
                ) : (
                  <img src={'/assets/shared/svg/AllowDisabled.svg'} width='24px' height='24px' />
                )}
              </ModelSecondaryButton>
            </Tooltip>
          </Col>
          <Col>
            <Tooltip
              title={
                <Grid>
                  <Typography className={classes.tooltipBold}>
                    {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED
                      ? 'Blocked Creative'
                      : 'Block Creative'}
                  </Typography>
                  <Typography className={classes.tooltipSmall}>
                    {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED
                      ? 'Click to unblock and review ad'
                      : 'Prevent this ad from serving altogether'}
                  </Typography>
                </Grid>
              }
            >
              <ModelSecondaryButton
                style={{
                  minWidth: '0',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => {
                  if (!updatingReviewStatus) {
                    this.toggleReviewStatus(rowData, REVIEW_STATUS_BLOCKED);
                  }
                }}
              >
                {/* Blocked Circle with Slash */}
                {rowData.ReviewStatus === REVIEW_STATUS_BLOCKED ||
                rowData.ReviewStatus === REVIEW_STATUS_UNREVIEWED ? (
                  <img src={'/assets/shared/svg/Block.svg'} width='24px' height='24px' />
                ) : (
                  <img src={'/assets/shared/svg/BlockDisabled.svg'} width='24px' height='24px' />
                )}
              </ModelSecondaryButton>
            </Tooltip>
          </Col>
          <Col>
            {/* Edit button */}
            <Tooltip
              title={
                <Grid>
                  <Typography className={this.props.classes.tooltipBold}>Edit</Typography>
                </Grid>
              }
            >
              <ModelSecondaryButton
                style={{
                  minWidth: '0',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onClick={() => this.handleEditCreativeReviewOpen(rowData)}
              >
                <img src={'/assets/shared/svg/EditUnderlined.svg'} width='24px' height='24px' />
              </ModelSecondaryButton>
            </Tooltip>
          </Col>
        </Cols>
      );
    }

    handleEditCreativeReviewOpen = (rowData: ICreativeValidationRowData) => {
      this.setState(
        {
          creativeData: rowData,
        },
        () =>
          this.setState({
            openEditCreativeReviewModal: true,
          }),
      );
    };

    handleEditCreativeReviewClose = () => {
      this.setState({
        openEditCreativeReviewModal: false,
      });
    };

    handleCreativeReviewMessage = (error: boolean, message: string) => {
      this.setState({
        snackbarError: error,
        snackbarMessage: message,
      });
    };

    handleRefreshCreativeReview = (rowData: ICreativeValidationRowData) => {
      this.setState({
        updatingCreativeReview: true,
      });

      this.switchCreativeValidationRefs(rowData.ReviewStatus);
    };

    handleNotesOpen = (rowData: ICreativeValidationRowData) => {
      this.setState({ openNotesDrawer: true, creativeData: rowData });
    };

    handleNotesClose = () => {
      this.setState({ openNotesDrawer: false });
    };

    handleRefreshNotes = (rowData: ICreativeValidationRowData) => {
      this.setState({
        updatingNotes: true,
      });

      this.switchCreativeValidationRefs(rowData.ReviewStatus);
    };

    renderNotes = (rowData: ICreativeValidationRowData) => {
      return (
        <Cols
          marginLeft={`-${spacing.large}`}
          style={{
            justifyContent: 'center',
          }}
        >
          <Col>
            <Tooltip
              title={
                <Grid>
                  <Typography className={this.props.classes.tooltipBold}>Notes</Typography>
                </Grid>
              }
            >
              <ModelSecondaryButton
                style={{ minWidth: '0', paddingLeft: '8px', paddingRight: '8px' }}
                onClick={() => this.handleNotesOpen(rowData)}
              >
                {/* Notes Icon */}
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 22 22'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M19 5V14H14V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H15L21 15V5C21 3.9 20.1 3 19 3ZM12 14H7V12H12V14ZM17 10H7V8H17V10Z'
                    fill='#646464'
                  />
                </svg>
              </ModelSecondaryButton>
            </Tooltip>
          </Col>
        </Cols>
      );
    };

    switchCreativeValidationRefs = (reviewStatus: number) => {
      switch (reviewStatus) {
        case REVIEW_STATUS_UNREVIEWED:
          this.creativeValidationUnreviewedTableRef.current.onQueryChange();
          return;
        case REVIEW_STATUS_REVIEWED:
          this.creativeValidationReviewedTableRef.current.onQueryChange();
          return;
        case REVIEW_STATUS_BLOCKED:
          this.creativeValidationBlockedTableRef.current.onQueryChange();
          return;
        case REVIEW_STATUS_ALLOWED:
          this.creativeValidationAllowedTableRef.current.onQueryChange();
          return;
        case REVIEW_STATUS_BLOCKED_BY_API:
          this.creativeValidationBlockedByAPIRef.current.onQueryChange();
          return;
        default:
          return;
      }
    };

    renderCreativeReview = () => {
      const { reviewStatusFilter } = this.state;

      const headerWidth = reviewStatusFilter === REVIEW_STATUS_UNREVIEWED ? '14%' : '19%';

      // default columns for creative validation tables
      const listTableColumns: IListTableColumn[] = [
        {
          field: 'CreativeURL',
          title: 'Creative URL',
          renderType: 'playCreativeURL',
          searchable: true,
          width: '35%',
        },
        {
          field: 'Adomain',
          title: 'Adomain',
          renderType: 'truncate',
          searchable: true,
          width: headerWidth,
        },
        {
          field: 'IABCategory',
          title: 'IAB Category',
          renderType: 'truncate2',
          searchable: true,
          width: headerWidth,
          cellStyle: { textAlign: 'left' },
        },
        {
          field: 'BidderID',
          title: 'Bidder ID',
          renderType: 'normal',
          searchable: false,
          sorting: true,
          width: headerWidth,
        },
        {
          field: 'Actions',
          title: 'Actions',
          render: (rowData: ICreativeValidationRowData) => this.renderCreativeURLAction(rowData),
          searchable: false,
          maxWidth: '100px',
          width: headerWidth,
        },
        {
          field: 'Notes',
          title: 'Notes',
          render: (rowData: ICreativeValidationRowData) => this.renderNotes(rowData),
          searchable: true,
          maxWidth: '100px',
          width: headerWidth,
        },
      ];

      // add blocked responses and impressions column when we query from the snowflake table when review status is unreviewed
      if (reviewStatusFilter === REVIEW_STATUS_UNREVIEWED) {
        listTableColumns.splice(ReviewOrderByBlockedResponses, 0, {
          field: 'BidErrorsBrandSafety',
          title: 'Blocked Responses (L24H)',
          renderType: 'normal',
          searchable: false,
          sorting: true,
          width: headerWidth,
        });
        listTableColumns.splice(ReviewOrderByImpressions, 0, {
          field: 'Impressions',
          title: 'Impressions (L24H)',
          renderType: 'normal',
          searchable: false,
          sorting: true,
          width: headerWidth,
        });
      }

      // add blocked responses column when we query from the snowflake table when review status is blocked
      if (reviewStatusFilter === REVIEW_STATUS_BLOCKED) {
        listTableColumns.splice(ReviewOrderByBlockedResponses, 0, {
          field: 'BidErrorBlockedResponses',
          title: 'Blocked Responses',
          renderType: 'normal',
          searchable: false,
          sorting: true,
          width: headerWidth,
        });
      }

      let tableRef = this.creativeValidationUnreviewedTableRef;
      if (reviewStatusFilter === REVIEW_STATUS_REVIEWED) {
        tableRef = this.creativeValidationReviewedTableRef;
      } else if (reviewStatusFilter === REVIEW_STATUS_BLOCKED) {
        tableRef = this.creativeValidationBlockedTableRef;
      } else if (reviewStatusFilter === REVIEW_STATUS_ALLOWED) {
        tableRef = this.creativeValidationAllowedTableRef;
      } else if (reviewStatusFilter === REVIEW_STATUS_BLOCKED_BY_API) {
        tableRef = this.creativeValidationBlockedByAPIRef;
      }

      return (
        <Grid container direction='column'>
          <Grid item style={{ marginLeft: 15, width: '100%' }}>
            <Typography style={{ marginTop: 10, marginBottom: 10, fontSize: 16, fontWeight: 400 }}>
              Review creatives that have been blocked and saved by a block rule. Impressions
              recorded below were served where no block rule applies.
            </Typography>
          </Grid>
          <Grid
            item
            style={{
              width: '100%',
              paddingBottom: spacing.large,
            }}
          >
            <ListTable
              title={''}
              resource='creative_validation'
              columns={listTableColumns}
              data={[]}
              fixedEnabled={true}
              tableRef={tableRef} // reference to the underlying table component
              enablePagination={true}
              onOrderChange={this.handelCreativeValidationReviewOrderChange}
              queryData={this.getCreativeValidations}
              updating={this.state.updatingReviewStatus}
              loading={this.state.updatingReviewStatus}
              pageSize={25}
              pageSizeOptions={reviewStatusFilter === REVIEW_STATUS_UNREVIEWED ? [25] : undefined}
              renderCustomToolbar={this.renderListViewToolbar}
              readRoleName={'read_channel_brandsafety_rule'}
              writeRoleName={'read_channel_brandsafety_rule'}
            />
          </Grid>
        </Grid>
      );
    };

    // ############################## MAIN RENDER ##############################
    render() {
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largeHorizontalPadding>
            <EditCreativeReviewModal
              classes={this.props.classes}
              openEditCreativeReviewModal={this.state.openEditCreativeReviewModal}
              handleClose={this.handleEditCreativeReviewClose}
              creativeData={this.state.creativeData}
              handleApplyFilterKeyDown={this.handleApplyFilterKeyDown}
              handleRefreshCreativeReview={this.handleRefreshCreativeReview}
              getCreativeValidations={this.getCreativeValidations}
              handleCreativeReviewMessage={this.handleCreativeReviewMessage}
              updatingReviewStatus={this.state.updatingReviewStatus}
              brandSafetyRules={this.state.rules}
              user={this.props.user.user}
              reviewStatusFilter={this.state.reviewStatusFilter}
            />
            <NotesDrawer
              openDrawer={this.state.openNotesDrawer}
              handleClose={this.handleNotesClose}
              creativeData={this.state.creativeData}
              handleRefreshNotes={this.handleRefreshNotes}
            />

            {this.renderTabs()}

            {/* ?? DELETE CONFIRMATION POPUP ?? */}
            <ModelDialogDeleteView
              active={this.state.deletePopupActive}
              model={this.isCurrentTab('Brand Safety Rules') ? 'Rule' : 'Tracker'}
              ID={this.state.deletePopupItemID}
              Name={this.state.deletePopupItemName}
              deleteModel={(id: string) => {
                return this.deleteItemWithPromise(Number(id));
              }}
              onDelete={this.onItemDelete}
              onError={this.hideDeleteDialog}
              onClose={this.hideDeleteDialog}
            />

            {/* SNACKBAR */}
            <SnackbarView
              open={!!this.state.snackbarMessage}
              error={this.state.snackbarError}
              onClose={this.snackbarClose}
            >
              {this.state.snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
